<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { messageStamp } from 'shared/helpers/timeHelper';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import CreateEnrichmentDialog from './create/CreateEnrichmentDialog.vue';
import DeleteEnrichmentDialog from './delete/DeleteEnrichmentDialog.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const createDialogRef = ref(null);
const deleteDialogRef = ref(null);

const eventAutomations = useMapGetter(
  'wgptEventAutomations/getEventAutomations'
);
const uiFlags = useMapGetter('wgptEventAutomations/getUIFlags');

const tableHeaders = computed(() => [
  t('WGPT_CUSTOM_REPORTS.ENRICHMENTS.TABLE.NAME'),
  t('WGPT_CUSTOM_REPORTS.ENRICHMENTS.TABLE.TRIGGER'),
  t('WGPT_CUSTOM_REPORTS.ENRICHMENTS.TABLE.STATUS'),
  t('WGPT_CUSTOM_REPORTS.ENRICHMENTS.UPDATED_ON'),
]);

const readableDate = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy');
const readableDateWithTime = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy hh:mm a');

function openCreateDialog() {
  createDialogRef.value?.open();
}

function navigateToEditor(eventAutomation) {
  router.push(
    accountScopedRoute('wgpt_reports_settings_enrichment_automation_editor', {
      eventAutomationId: eventAutomation.id,
    })
  );
}

function onCreated(record) {
  if (record) navigateToEditor(record);
}

function triggerLabel(trigger) {
  if (!trigger?.type) return '—';
  const labels = {
    custom_event_logged: t(
      'WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.FORM.TYPE_OPTIONS.CUSTOM_EVENT_LOGGED'
    ),
    event_enrichment_scheduled: t(
      'WGPT_CUSTOM_REPORTS.CREATE_ENRICHMENT_DIALOG.FORM.TYPE_OPTIONS.EVENT_ENRICHMENT_SCHEDULED'
    ),
  };
  return labels[trigger.type] || trigger.type;
}

async function toggleActive(record) {
  const active = !record.active;

  if (
    active &&
    record.trigger?.type === 'event_enrichment_scheduled' &&
    !record.trigger?.attributes?.cron
  ) {
    useAlert(
      t(
        'WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.VALIDATION.CRON_REQUIRED'
      )
    );
    return;
  }

  try {
    await store.dispatch('wgptEventAutomations/update', {
      id: record.id,
      wgpt_automation_attributes: { active },
    });
    useAlert(
      t('WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.API.SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_CUSTOM_REPORTS.ENRICHMENT_AUTOMATION_EDITOR.API.ERROR_MESSAGE')
    );
  }
}

onMounted(() => {
  store.dispatch('wgptEventAutomations/get');
});

defineExpose({ openCreateDialog });
</script>

<template>
  <div>
    <woot-loading-state v-if="uiFlags.isFetching" />

    <p
      v-else-if="!eventAutomations.length"
      class="flex-1 py-20 text-n-slate-12 flex items-center justify-center text-base"
    >
      {{ t('WGPT_CUSTOM_REPORTS.ENRICHMENTS.EMPTY_TITLE') }}
    </p>

    <BaseTable v-else :headers="tableHeaders" :items="eventAutomations">
      <template #row="{ items }">
        <BaseTableRow v-for="record in items" :key="record.id" :item="record">
          <template #default>
            <BaseTableCell class="max-w-0 w-full">
              <span class="block text-heading-3 text-n-slate-12">
                {{ record.name }}
              </span>
            </BaseTableCell>

            <BaseTableCell>
              <span class="text-body-main text-n-slate-11 whitespace-nowrap">
                {{ triggerLabel(record.trigger) }}
              </span>
            </BaseTableCell>

            <BaseTableCell>
              <ToggleSwitch
                :model-value="record.active"
                @click.stop="toggleActive(record)"
              />
            </BaseTableCell>

            <BaseTableCell :title="readableDateWithTime(record.updated_at)">
              <span class="text-body-main text-n-slate-12 whitespace-nowrap">
                {{ readableDate(record.updated_at) }}
              </span>
            </BaseTableCell>

            <BaseTableCell align="end">
              <div class="flex gap-3 justify-end flex-shrink-0">
                <Button
                  v-tooltip.top="
                    $t('WGPT_CUSTOM_REPORTS.ENRICHMENTS.TOOLTIP.EDIT')
                  "
                  icon="i-woot-edit-pen"
                  sm
                  slate
                  @click.stop="navigateToEditor(record)"
                />
                <Button
                  v-tooltip.top="
                    $t('WGPT_CUSTOM_REPORTS.ENRICHMENTS.TOOLTIP.DELETE')
                  "
                  icon="i-woot-bin"
                  sm
                  slate
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  @click.stop="deleteDialogRef.open(record)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <CreateEnrichmentDialog ref="createDialogRef" @created="onCreated" />
    <DeleteEnrichmentDialog ref="deleteDialogRef" />
  </div>
</template>
